if (!customElements.get('corporate-order-confirmation')) {
  const POLL_INTERVAL_MS = 10000;
  const MAX_POLLS = 30; /* ~5 minutes of webhook/split lag coverage */
  /* Order statuses where the split is still in flight and the page keeps polling. */
  const SETTLING_STATUSES = ['draft', 'paid', 'splitting'];

  class CorporateOrderConfirmation extends HTMLElement {
    connectedCallback() {
      this.loadingEl = this.querySelector('[data-cbp-loading]');
      this.errorEl = this.querySelector('[data-cbp-error]');
      this.contentEl = this.querySelector('[data-cbp-content]');
      this.processingEl = this.querySelector('[data-cbp-processing]');
      this.metaEl = this.querySelector('[data-cbp-meta]');
      this.itemsEl = this.querySelector('[data-cbp-items]');
      this.taxRowEl = this.querySelector('[data-cbp-tax-row]');
      this.taxEl = this.querySelector('[data-cbp-tax]');
      this.totalEl = this.querySelector('[data-cbp-total]');
      this.countEl = this.querySelector('[data-cbp-count]');
      this.recipientsEl = this.querySelector('[data-cbp-recipients]');

      this.pollCount = 0;
      this.pollTimer = null;
      this.rendered = false;
      this.variantIndexPromise = null;

      this.token = (new URLSearchParams(window.location.search).get('token') || '').trim();

      if (!this.apiBase()) {
        this.showError(this.t('apiMissing'));
        return;
      }
      if (!this.token) {
        this.showError(this.t('errorNotFound'));
        return;
      }
      this.load();
    }

    disconnectedCallback() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
    }

    apiBase() {
      return (this.dataset.apiBase || '').replace(/\/$/, '');
    }

    async load() {
      let order = null;
      try {
        const response = await fetch(
          this.apiBase() + '/api/corporate/orders/' + encodeURIComponent(this.token),
          { headers: { Accept: 'application/json' } }
        );
        if (response.status === 404) {
          this.showError(this.t('errorNotFound'));
          return;
        }
        if (!response.ok) throw new Error('http_' + response.status);
        const data = await response.json();
        if (!data || !data.ok || !data.order) throw new Error('bad_payload');
        order = data.order;
        await this.render(order);
      } catch (e) {
        /* A failed poll after a successful render keeps the last good view. */
        if (!this.rendered) {
          this.showError(this.t('errorGeneric'));
          return;
        }
      }
      this.scheduleNextPoll(order);
    }

    scheduleNextPoll(order) {
      const settling =
        !order ||
        SETTLING_STATUSES.indexOf(order.status) !== -1 ||
        (order.recipients || []).some((r) => r.status === 'pending');
      if (!settling || this.pollCount >= MAX_POLLS) return;
      this.pollCount += 1;
      this.pollTimer = setTimeout(() => this.load(), POLL_INTERVAL_MS);
    }

    showError(message) {
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.contentEl) this.contentEl.hidden = true;
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    async render(order) {
      this.rendered = true;
      /* Landing here from the confirmation email is proof the order went
         through: retire the basket and contact sheet it was built from. Scoped
         to this token so an old email link cannot wipe a newer basket. */
      if (window.WildgrainCorporateBasket && window.WildgrainCorporateBasket.isPlaced(order.status)) {
        window.WildgrainCorporateBasket.clearPlacedOrder(this.token);
      }
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.contentEl) this.contentEl.hidden = false;
      if (this.processingEl) {
        this.processingEl.hidden = SETTLING_STATUSES.indexOf(order.status) === -1;
      }

      this.renderMeta(order);
      this.renderRecipients(order);
      await this.renderItems(order);
    }

    renderMeta(order) {
      if (this.metaEl) {
        this.metaEl.textContent = '';
        const rows = [];
        if (order.parent_order_name) rows.push([this.t('orderLabel'), order.parent_order_name]);
        if (order.created_at) {
          const date = this.formatDate(order.created_at);
          if (date) rows.push([this.t('placedLabel'), date]);
        }
        if (order.company) rows.push([this.t('companyLabel'), order.company]);
        if (order.ship_date) {
          const picked = this.formatDay(order.ship_date);
          if (picked) rows.push([this.t('holdUntilLabel'), picked]);
        }

        rows.forEach(([label, value]) => {
          const row = document.createElement('div');
          row.className = 'cbp-meta__row';
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = value;
          row.append(dt, dd);
          this.metaEl.appendChild(row);
        });
      }

      if (this.totalEl) {
        this.totalEl.textContent = order.total_price != null ? this.formatMoney(order.total_price) : '';
      }
      /* Collected destination tax — hidden for pre-tax orders and $0 totals. */
      if (this.taxRowEl && this.taxEl) {
        const taxTotal = Number(order.tax_total);
        if (Number.isFinite(taxTotal) && taxTotal > 0) {
          this.taxEl.textContent = this.formatMoney(taxTotal);
          this.taxRowEl.hidden = false;
        } else {
          this.taxRowEl.hidden = true;
        }
      }
      if (this.countEl) {
        this.countEl.textContent = order.recipient_count ? '(' + order.recipient_count + ')' : '';
      }
    }

    async renderItems(order) {
      if (!this.itemsEl) return;
      const variantIndex = await this.ensureVariantIndex();
      this.itemsEl.textContent = '';
      (order.basket || []).forEach((item) => {
        const li = document.createElement('li');
        li.className = 'cbp-item';

        const quantity = document.createElement('span');
        quantity.className = 'cbp-item__quantity';
        quantity.textContent = (Number(item.quantity) || 1) + ' ×';

        const title = document.createElement('span');
        title.className = 'cbp-item__title';
        const entry = variantIndex[item.variant_id];
        title.textContent = item.title || (entry ? entry.title : this.t('giftItem'));

        li.append(quantity, title);
        this.itemsEl.appendChild(li);
      });
    }

    renderRecipients(order) {
      if (!this.recipientsEl) return;
      this.recipientsEl.textContent = '';
      (order.recipients || []).forEach((recipient) => {
        const li = document.createElement('li');
        li.className = 'cbp-recipient';

        const info = document.createElement('div');
        info.className = 'cbp-recipient__info';

        const name = document.createElement('span');
        name.className = 'cbp-recipient__name';
        name.textContent = [recipient.first_name, recipient.last_name].filter(Boolean).join(' ');
        info.appendChild(name);

        const location = [recipient.city, recipient.state].filter(Boolean).join(', ');
        if (location) {
          const locationEl = document.createElement('span');
          locationEl.className = 'cbp-recipient__location';
          locationEl.textContent = location;
          info.appendChild(locationEl);
        }

        /* Per-box destination tax ($0.00 shown too — exempt states are
           informative). Hidden only for pre-tax orders (null). */
        const taxAmount = Number(recipient.tax_amount);
        if (recipient.tax_amount != null && Number.isFinite(taxAmount)) {
          const taxEl = document.createElement('span');
          taxEl.className = 'cbp-recipient__tax';
          taxEl.textContent = this.t('recipientTax') + ': ' + this.formatMoney(taxAmount);
          info.appendChild(taxEl);
        }

        const actions = document.createElement('div');
        actions.className = 'cbp-recipient__actions';

        const status = this.statusInfo(recipient.status, recipient.tracking_url);
        const pill = document.createElement('span');
        pill.className = 'cbp-pill cbp-pill--' + status.modifier;
        pill.textContent = status.label;
        actions.appendChild(pill);

        const trackingHref = this.trackingHref(recipient.tracking_url);
        if (trackingHref) {
          const track = document.createElement('a');
          track.className = 'cbp-track';
          track.href = trackingHref;
          track.target = '_blank';
          track.rel = 'noopener noreferrer';
          track.textContent = this.t('track');
          actions.appendChild(track);
        }

        li.append(info, actions);
        this.recipientsEl.appendChild(li);
      });
    }

    statusInfo(status, trackingUrl) {
      switch (status) {
        case 'created':
          return { label: this.t('statusPlaced'), modifier: 'placed' };
        case 'shipped':
          return { label: this.t('statusShipped'), modifier: 'shipped' };
        case 'delivered':
          return { label: this.t('statusDelivered'), modifier: 'delivered' };
        case 'cancelled':
          return { label: this.t('statusCancelled'), modifier: 'cancelled' };
        case 'failed':
          return { label: this.t('statusAttention'), modifier: 'attention' };
        default:
          /* A tracking link means the box has left the warehouse — never
             keep showing Preparing just because the theme predates a status. */
          if (trackingUrl) return { label: this.t('statusShipped'), modifier: 'shipped' };
          return { label: this.t('statusPreparing'), modifier: 'preparing' };
      }
    }

    /* Carrier URLs from Grip/Shopify should already be absolute; if a
       number slips through, don't turn it into a same-origin path. */
    trackingHref(raw) {
      const value = String(raw || '').trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;
      if (/^\/\//.test(value)) return 'https:' + value;
      return '';
    }

    /* variant_id -> product title, resolved from the storefront (best effort). */
    ensureVariantIndex() {
      if (!this.variantIndexPromise) {
        this.variantIndexPromise = fetch('/products.json?limit=250', {
          headers: { Accept: 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : { products: [] }))
          .then((data) => {
            const index = {};
            (data.products || []).forEach((product) => {
              (product.variants || []).forEach((variant) => {
                const suffix =
                  variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : '';
                index[variant.id] = { title: product.title + suffix };
              });
            });
            return index;
          })
          .catch(() => ({}));
      }
      return this.variantIndexPromise;
    }

    /* API money values are dollars (unlike the cents used by cart JS). */
    formatMoney(amount) {
      const value = Number(amount).toFixed(2);
      const format = this.dataset.moneyFormat || '${{amount}}';
      return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
    }

    formatDate(iso) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    formatDay(value) {
      const ymd = String(value || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return this.formatDate(value);
      const parts = ymd.split('-').map(Number);
      return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }

    /* Shopify auto-escapes {{ }} and `| escape` on top of that leaves
       literal &#39; in data-text-* attributes (Don't / couldn't). */
    decodeText(value) {
      return String(value || '')
        .replace(/&#39;|&apos;|&#x27;/gi, "'")
        .replace(/&quot;|&#34;|&#x22;/gi, '"')
        .replace(/&amp;/g, '&');
    }

    t(key) {
      const fallbacks = {
        errorNotFound:
          'We couldn\u2019t find this gift order. Check the link from your confirmation email, or contact us.',
        errorGeneric: 'Something went wrong loading your gift order. Please try again in a moment.',
        apiMissing: 'This page is not configured. Set the API base URL in the theme section settings.',
        orderLabel: 'Order',
        placedLabel: 'Placed',
        companyLabel: 'Company',
        holdUntilLabel: "Don't ship before",
        statusPreparing: 'Preparing',
        statusPlaced: 'Order placed',
        statusShipped: 'Shipped',
        statusDelivered: 'Delivered',
        statusAttention: 'Needs attention',
        statusCancelled: 'Cancelled',
        track: 'Track',
        giftItem: 'Gift item',
        recipientTax: 'Sales tax',
      };
      const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
      return this.decodeText(this.dataset[attr] || fallbacks[key]);
    }
  }

  customElements.define('corporate-order-confirmation', CorporateOrderConfirmation);
}
